/**
 * MINI GERENCIAMENTO — CHAMADA.
 *
 * Escolhe turma + data e mostra a lista. Três estados (presente / falta /
 * falta justificada), com "marcar todos presentes" e correção: salvar de novo
 * no mesmo dia ATUALIZA (não duplica). Mostra também a frequência do aluno.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dica } from '../common/Formulario';
import { toast } from 'sonner';
import { CheckCheck, Eraser, Loader2, Save, UserCheck } from 'lucide-react';
import { minhaEquipeApi } from '../../lib/api';
import { STATUS_PRESENCA, resumoFrequencia, turmaPadraoDoDia } from '../../lib/minhaEquipe';
import { hojeIso } from '../../lib/graduacao';

export default function ChamadaEquipe({ turmas = [] }) {
  const [turmaId, setTurmaId] = useState('');
  const [data, setData] = useState(hojeIso());
  const [alunos, setAlunos] = useState([]);
  const [jaLancada, setJaLancada] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [frequencia, setFrequencia] = useState({});
  const [podeLancar, setPodeLancar] = useState(true);

  const turma = turmas.find((t) => t.id === turmaId);

  // PRÉ-SELEÇÃO: com uma turma só (ou uma única com aula hoje) a chamada já abre
  // na turma do professor — sem escolher nada. Só preenche se o campo estiver vazio.
  useEffect(() => {
    const padrao = turmaPadraoDoDia(turmas, data);
    if (!turmaId && padrao) setTurmaId(padrao);
  }, [turmas, data, turmaId]);

  const carregar = useCallback(async () => {
    if (!turmaId || !data) { setAlunos([]); return; }
    setCarregando(true);
    try {
      const { data: resposta } = await minhaEquipeApi.chamada({ class_group_id: turmaId, date: data });
      setAlunos((resposta.alunos || []).map((a) => ({ ...a, status: a.status || '' })));
      setJaLancada(!!resposta.ja_lancada);
      setPodeLancar(resposta.pode_lancar !== false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar a chamada');
      setAlunos([]);
    } finally {
      setCarregando(false);
    }
  }, [turmaId, data]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    // A frequência de cada aluno aparece junto da chamada (o professor vê o histórico).
    if (!alunos.length) { setFrequencia({}); return; }
    let ativo = true;
    Promise.all(alunos.map((a) => minhaEquipeApi.frequencia(a.athlete_id)
      .then((r) => [a.athlete_id, r.data])
      .catch(() => [a.athlete_id, null])))
      .then((pares) => { if (ativo) setFrequencia(Object.fromEntries(pares)); });
    return () => { ativo = false; };
  }, [alunos]);

  const marcarTodos = () => {
    if (!podeLancar) return;
    setAlunos((prev) => prev.map((a) => ({ ...a, status: 'present' })));
  };
  const limpar = () => setAlunos((prev) => prev.map((a) => ({ ...a, status: '' })));
  const marcar = (athleteId, status) => {
    if (!podeLancar) return;
    setAlunos((prev) => prev.map(
      (a) => (a.athlete_id === athleteId ? { ...a, status } : a),
    ));
  };

  const salvar = async () => {
    const marcados = alunos.filter((a) => a.status);
    if (!marcados.length) { toast.error('Marque pelo menos um aluno'); return; }
    setSalvando(true);
    try {
      const { data: resposta } = await minhaEquipeApi.salvarChamada({
        class_group_id: turmaId,
        date: data,
        items: marcados.map((a) => ({ athlete_id: a.athlete_id, status: a.status })),
      });
      toast.success(`Chamada salva (${resposta.registros} registro(s))`);
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar a chamada');
    } finally {
      setSalvando(false);
    }
  };

  const apagar = async () => {
    if (!window.confirm(`Apagar a chamada de ${data} desta turma?`)) return;
    try {
      const { data: resposta } = await minhaEquipeApi.apagarChamada({ class_group_id: turmaId, date: data });
      toast.success(`Chamada apagada (${resposta.registros} registro(s))`);
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar a chamada');
    }
  };

  if (!turmas.length) {
    return (
      <p className="text-sm text-[#71717A]" data-testid="chamada-sem-turma">
        Crie uma turma primeiro (aba Turmas) para poder fazer a chamada.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="chamada-equipe">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-[#A1A1AA] text-xs">Turma</Label>
          <Select value={turmaId || 'none'} onValueChange={(v) => setTurmaId(v === 'none' ? '' : v)}>
            <SelectTrigger data-testid="chamada-turma" className="bg-[#1A1A1A] border-[#27272A] text-white">
              <SelectValue placeholder="Selecione a turma" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
              <SelectItem value="none" className="text-white">Selecione...</SelectItem>
              {turmas.map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#A1A1AA] text-xs">Data da aula</Label>
          <Input type="date" value={data} max={hojeIso()} onChange={(e) => setData(e.target.value)}
                 data-testid="chamada-data" className="bg-[#1A1A1A] border-[#27272A] text-white" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={marcarTodos} data-testid="chamada-todos"
                  className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            <CheckCheck size={16} className="mr-1" /> Todos presentes
          </Button>
          <Button variant="outline" onClick={limpar} data-testid="chamada-limpar"
                  className="border-[#27272A] text-[#A1A1AA] hover:text-white">
            <Eraser size={16} />
          </Button>
        </div>
      </div>

      {jaLancada && (
        <p className="text-micro text-emerald-400" data-testid="chamada-ja-lancada">
          Chamada deste dia já lançada — salvar de novo corrige (não duplica).
        </p>
      )}

      {/* Quem só PODE VER a turma vê a chamada, mas não lança (o servidor recusa). */}
      {!podeLancar && turmaId && (
        <p className="text-micro text-amber-400" data-testid="chamada-somente-leitura">
          Você pode ver esta turma, mas não lançar a chamada: só quem criou, o responsável
          pela equipe ou o professor da turma pode marcar presença.
        </p>
      )}

      {carregando ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
      ) : !turmaId ? (
        <p className="text-sm text-[#71717A]">Escolha a turma e a data para fazer a chamada.</p>
      ) : alunos.length === 0 ? (
        <p className="text-sm text-[#71717A]" data-testid="chamada-vazia">
          Esta turma ainda não tem alunos. Edite a turma na aba Turmas para incluir.
        </p>
      ) : (
        <div className="rounded-lg border border-[#27272A] divide-y divide-[#27272A]" data-testid="chamada-lista">
          {alunos.map((a) => (
            <div key={a.athlete_id} className="flex items-center justify-between gap-3 p-2.5">
              <div className="min-w-0">
                <p className="text-sm text-white truncate">
                  {a.name} <span className="text-[#71717A] text-xs">· {a.belt}</span>
                </p>
                <Dica icone={false}>
                  {resumoFrequencia(frequencia[a.athlete_id])}
                </Dica>
              </div>
              <div className="flex gap-1 shrink-0">
                {STATUS_PRESENCA.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => marcar(a.athlete_id, a.status === s.value ? '' : s.value)}
                    data-testid={`chamada-${a.athlete_id}-${s.value}`}
                    className={`px-2.5 py-1 rounded text-micro border ${
                      a.status === s.value
                        ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold'
                        : 'border-[#27272A] text-[#A1A1AA] hover:text-white'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barra fixa no rodapé: com turma cheia o "Salvar chamada" ficava no
          fim da lista de alunos, fora da tela. */}
      {turmaId && alunos.length > 0 && (
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-[#27272A] bg-[#0A0A0A] py-2">
          <p className="text-micro text-[#71717A] flex items-center gap-1">
            <UserCheck size={12} />
            {alunos.filter((a) => a.status).length} de {alunos.length} marcado(s)
            {(turma?.weekdays || []).length > 0 ? ` · turma: ${turma.name}` : ''}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={apagar} disabled={!jaLancada || !podeLancar}
                    data-testid="chamada-apagar"
                    className="border-[#27272A] text-red-400 hover:text-red-300">
              Apagar chamada do dia
            </Button>
            <Button onClick={salvar} disabled={salvando || !podeLancar} data-testid="chamada-salvar"
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} className="mr-1" />}
              Salvar chamada
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
